import { Configuration, LogLevel } from '@azure/msal-browser';
import { logger } from '@utils/logger';

const envTenant = (import.meta as any).env?.VITE_CIAM_TENANT_SUBDOMAIN as
  | string
  | undefined;
const envClientId = (import.meta as any).env?.VITE_CLIENT_ID as
  | string
  | undefined;
const envPolicy = (import.meta as any).env?.VITE_SIGN_IN_POLICY as
  | string
  | undefined;
const envAuthority = (import.meta as any).env?.VITE_AUTHORITY as
  | string
  | undefined;
const envRedirectUri = (import.meta as any).env?.VITE_REDIRECT_URI as
  | string
  | undefined;
const envPostLogoutRedirectUri = (import.meta as any).env
  ?.VITE_POST_LOGOUT_REDIRECT_URI as string | undefined;

export const CIAM_TENANT_SUBDOMAIN: string =
  envTenant || 'Enter_the_Tenant_Subdomain_Here';

export const CLIENT_ID: string = envClientId || 'Enter_the_Client_id_Here';

export const SIGN_IN_POLICY: string =
  envPolicy || 'Enter_the_SignUpSignIn_User_Flow_Name_Here';

const placeholdersUsed =
  CIAM_TENANT_SUBDOMAIN.includes('Enter_the_') ||
  CLIENT_ID.includes('Enter_the_') ||
  SIGN_IN_POLICY.includes('Enter_the_');

if (!envTenant || !envClientId || !envPolicy || placeholdersUsed) {
  logger.error(
    `\n[authConfig] MSAL is not fully configured.\n` +
      `- VITE_CIAM_TENANT_SUBDOMAIN: ${envTenant ?? '(missing)'}\n` +
      `- VITE_CLIENT_ID: ${envClientId ?? '(missing)'}\n` +
      `- VITE_SIGN_IN_POLICY: ${envPolicy ?? '(missing)'}\n\n` +
      `Fix: Create .env.local with values, for example:\n` +
      `VITE_CIAM_TENANT_SUBDOMAIN=contoso\n` +
      `VITE_CLIENT_ID=00000000-0000-0000-0000-000000000000\n` +
      `VITE_SIGN_IN_POLICY=B2C_1_signupsignin\n\n` +
      `After updating, restart the dev server (npm run dev).`
  );
}

const defaultAuthority = 'https://auxcustomerslegacy.ciamlogin.com/';
export const authority = envAuthority
  ? envAuthority
  : defaultAuthority ||
    `https://${CIAM_TENANT_SUBDOMAIN}.ciamlogin.com/${CIAM_TENANT_SUBDOMAIN}.onmicrosoft.com/${SIGN_IN_POLICY}`;

function getKnownAuthorityHost(authUrl: string) {
  try {
    const u = new URL(authUrl);
    return u.hostname;
  } catch {
    return `${CIAM_TENANT_SUBDOMAIN}.ciamlogin.com`;
  }
}

function getRedirectUri() {
  if (envRedirectUri) return envRedirectUri;
  return window.location.origin + '/microsoft/callback';
}
function getPostLogoutRedirectUri() {
  if (envPostLogoutRedirectUri) return envPostLogoutRedirectUri;
  return '/';
}

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority,
    knownAuthorities: [getKnownAuthorityHost(authority)],
    redirectUri: getRedirectUri(),
    postLogoutRedirectUri: getPostLogoutRedirectUri(),
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        logger.log(message);
        if (containsPii) {
          logger.warn(message);
          return;
        }
        switch (level) {
          case LogLevel.Error:
            logger.error(message);
            return;
          case LogLevel.Info:
            logger.info(message);
            return;
          case LogLevel.Verbose:
            logger.debug(message);
            return;
          case LogLevel.Warning:
            logger.warn(message);
            return;
          default:
            return;
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

export const loginRequest = {
  scopes: ['openid', 'profile', 'email'], // add API scopes later if needed
};
